import React from 'react'
import { Controller, FieldErrorsImpl, useFormContext } from 'react-hook-form'
import { Group, Select, Stack, TextInput } from '@mantine/core'

import { AttendeeCard } from '@/components/Event/NewScript/components/common/AttendeeCard/AttendeeCard'
import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import { IdentityDocumentInput } from '@/components/Event/NewScript/components/common/IdentityDocumentCard/IdentityDocumentInput'
import {
  ActionContainer,
  ActionText,
  ActionTitle,
} from '@/components/Event/NewScript/components/common/ScriptTimeline'
import { getFieldName } from '@/components/Event/NewScript/utils/attendees'
import {
  IDENTITY_DOCUMENT_STIPULATION_STATUSES,
  IdentityDocumentStatus,
} from '@/constants'
import { Event, ScriptAttendee } from '@/types'
import { STATES } from '@/utils/states'

type Props = {
  witness: ScriptAttendee
  namePrefix: string
  attendeeChanged: (attendee: ScriptAttendee) => void
  event: Event
  isWitnessLocationRequired: boolean
  isIdentityVerificationRequired: boolean
  allAttendees: ScriptAttendee[]
}

export const WitnessInput: React.FC<Props> = ({
  witness,
  namePrefix,
  attendeeChanged,
  event,
  isWitnessLocationRequired,
  isIdentityVerificationRequired,
  allAttendees,
}) => {
  const { register, control, formState } = useFormContext()

  const { error } = control.getFieldState(
    getFieldName('', namePrefix),
    formState,
  ) as ReturnType<typeof control.getFieldState> & {
    error: FieldErrorsImpl<ScriptAttendee>
  }

  const { identity_document: identityDocument } = witness
  const shouldShowStipulation = IDENTITY_DOCUMENT_STIPULATION_STATUSES.includes(
    identityDocument?.status as IdentityDocumentStatus,
  )

  return (
    <>
      <ActionContainer outer>
        <Stack>
          <ActionText mono>
            Could the witness please state and spell their full name for the
            record?
          </ActionText>
          <AttendeeCard
            event={event}
            attendee={witness}
            onSubmitClick={attendeeChanged}
            namePrefix={namePrefix}
            allAttendees={allAttendees}
          />
        </Stack>
      </ActionContainer>
      {isIdentityVerificationRequired && (
        <ActionContainer outer>
          <Stack>
            <ActionText mono>
              Mr./Ms. {witness.name}, can you please hold a valid government
              photo identification up to the screen to verify your identity?
            </ActionText>
            <ActionContainer>
              <IdentityDocumentInput
                attendee={witness}
                namePrefix={`${namePrefix}.identity_document`}
                hideButton
                onSubmitClick={attendeeChanged}
              />
            </ActionContainer>
          </Stack>
        </ActionContainer>
      )}
      {isIdentityVerificationRequired && shouldShowStipulation && (
        <ActionContainer outer>
          <ActionText mono>
            For the record, the witness has not presented a valid ID for
            verification. Accordingly, do all parties stipulate to my
            administering the oath to this witness?
          </ActionText>
        </ActionContainer>
      )}
      {isWitnessLocationRequired && (
        <ActionContainer outer>
          <Stack>
            <ActionText mono>
              And finally, can the witness please confirm the city and state in
              which they are currently located?
            </ActionText>
            <ActionContainer>
              <Stack>
                <Group grow>
                  <TextInput
                    label="City"
                    withAsterisk
                    {...register(getFieldName('city', namePrefix))}
                    error={!!error?.city?.message}
                  />
                  <Controller
                    control={control}
                    name={getFieldName('state', namePrefix)}
                    render={({ field }) => (
                      <Select
                        {...field}
                        error={!!error?.state?.message}
                        label="State"
                        withAsterisk
                        placeholder="Select state"
                        searchable
                        data={Object.keys(STATES).map(key => ({
                          value: key,
                          label: STATES[key],
                        }))}
                      />
                    )}
                  />
                </Group>
                <FormErrors<ScriptAttendee>
                  errors={{ city: error?.city, state: error?.state }}
                />
              </Stack>
            </ActionContainer>
            <ActionContainer>
              <ActionTitle>Why do we ask this?</ActionTitle>
              <ActionText>
                We need the city/state of the deponent to add to the finalized
                transcript when it is ordered.
              </ActionText>
            </ActionContainer>
          </Stack>
        </ActionContainer>
      )}
    </>
  )
}
